/**
 * briefingsService — daily briefings read API.
 *
 * Hits GET /briefings backed by notches-api-get-briefings Lambda + DDB
 * notches-daily-briefings-prod.
 *
 * Optional auth. Anonymous requests get { publicMarkdown, gatedTeaser:true }.
 * Verified Premier requests also get { gatedMarkdown }.
 *
 * See docs/wiki/decisions/2026-07-28-daily-briefings-strategy.md
 */
import axios from 'axios';
import {
  FIXTURE_NAME as DETERMINISTIC_SEPT21_FIXTURE_NAME,
  fixtureFor as deterministicSept21FixtureFor,
} from './briefingFixtures/sept21Deterministic';
import {
  FIXTURE_NAME as ASOF_REPLAY_20260918_FIXTURE_NAME,
  fixtureFor as asOfReplay20260918FixtureFor,
} from './briefingFixtures/asOfReplay20260918';
import {
  FIXTURE_NAME as FULL_BRIEFING_20260918_FIXTURE_NAME,
  fixtureFor as fullBriefing20260918FixtureFor,
} from './briefingFixtures/fullBriefing20260918';
import {
  FIXTURE_NAME as PIPELINE_DETERMINISTIC_FULL_20260918_FIXTURE_NAME,
  fixtureFor as pipelineDeterministicFull20260918FixtureFor,
} from './briefingFixtures/pipelineDeterministicFull20260918';
import {
  FIXTURE_NAME as PIPELINE_DETERMINISTIC_FULL_20260925_FIXTURE_NAME,
  fixtureFor as pipelineDeterministicFull20260925FixtureFor,
} from './briefingFixtures/pipelineDeterministicFull20260925';
import {
  FIXTURE_NAME as PIPELINE_RESTORED_20260928_FIXTURE_NAME,
  fixtureFor as pipelineRestored20260928FixtureFor,
} from './briefingFixtures/pipelineRestored20260928';

// Prefer REACT_APP_PUBLIC_API_URL so a local build pointed at a test
// environment (which has no read Lambdas) can still hit prod for public
// content. Falls back to REACT_APP_API_URL for prod builds where the two
// are the same, then to a hardcoded prod URL for unset environments.
const API_BASE_URL =
  process.env.REACT_APP_PUBLIC_API_URL ||
  process.env.REACT_APP_API_URL ||
  'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

/**
 * Development-only fixture switch. Serves the September 21 deterministic
 * pilot pair through this service instead of hitting the Lambda.
 *
 * SAFETY: default OFF. The activation predicate is
 *   process.env.NODE_ENV !== 'production'
 *     && process.env.REACT_APP_BRIEFING_FIXTURE === 'deterministic-sept21'
 * `process.env.NODE_ENV === 'production'` is inlined as a compile-time
 * constant by CRA/webpack, so `react-scripts build` (which sets
 * NODE_ENV=production) statically eliminates this entire branch —
 * `REACT_APP_BRIEFING_FIXTURE` alone cannot enable fixture data in a
 * production bundle.
 */
const KNOWN_FIXTURES = [
  DETERMINISTIC_SEPT21_FIXTURE_NAME,
  ASOF_REPLAY_20260918_FIXTURE_NAME,
  FULL_BRIEFING_20260918_FIXTURE_NAME,
  PIPELINE_DETERMINISTIC_FULL_20260918_FIXTURE_NAME,
  PIPELINE_DETERMINISTIC_FULL_20260925_FIXTURE_NAME,
  PIPELINE_RESTORED_20260928_FIXTURE_NAME,
] as const;
type KnownFixture = typeof KNOWN_FIXTURES[number];

function activeFixture(): KnownFixture | null {
  if (process.env.NODE_ENV === 'production') return null;
  const requested = process.env.REACT_APP_BRIEFING_FIXTURE;
  if (!requested) return null;
  for (const name of KNOWN_FIXTURES) {
    if (requested === name) return name;
  }
  return null;
}

function warnFixtureActive(): void {
  const active = activeFixture();
  if (active && typeof console !== 'undefined') {
    // eslint-disable-next-line no-console
    console.info(
      `[briefingsService] dev fixture "${active}" is ACTIVE — ` +
        'the frontend is reading saved deterministic evidence, not the live API. ' +
        'Unset REACT_APP_BRIEFING_FIXTURE to restore normal behaviour.',
    );
  }
}
warnFixtureActive();

/** Dispatch date+variant to the matching dev fixture; return null when
 * no active fixture covers the request. Sept 21 pilot and Sept 18
 * as-of replay serve independently. */
function fixtureLookup(date: string, variant: BriefingVariant): BriefingFull | null {
  const active = activeFixture();
  if (active === DETERMINISTIC_SEPT21_FIXTURE_NAME) {
    return deterministicSept21FixtureFor(date, variant);
  }
  if (active === ASOF_REPLAY_20260918_FIXTURE_NAME) {
    return asOfReplay20260918FixtureFor(date, variant);
  }
  if (active === FULL_BRIEFING_20260918_FIXTURE_NAME) {
    return fullBriefing20260918FixtureFor(date, variant);
  }
  if (active === PIPELINE_DETERMINISTIC_FULL_20260918_FIXTURE_NAME) {
    return pipelineDeterministicFull20260918FixtureFor(date, variant);
  }
  if (active === PIPELINE_DETERMINISTIC_FULL_20260925_FIXTURE_NAME) {
    return pipelineDeterministicFull20260925FixtureFor(date, variant);
  }
  if (active === PIPELINE_RESTORED_20260928_FIXTURE_NAME) {
    return pipelineRestored20260928FixtureFor(date, variant);
  }
  return null;
}

/** Which date each fixture treats as "latest". */
function fixtureLatestDate(): string | null {
  const active = activeFixture();
  if (active === DETERMINISTIC_SEPT21_FIXTURE_NAME) return '2026-09-21';
  if (active === ASOF_REPLAY_20260918_FIXTURE_NAME) return '2026-09-18';
  if (active === FULL_BRIEFING_20260918_FIXTURE_NAME) return '2026-09-18';
  if (active === PIPELINE_DETERMINISTIC_FULL_20260918_FIXTURE_NAME) return '2026-09-18';
  if (active === PIPELINE_DETERMINISTIC_FULL_20260925_FIXTURE_NAME) return '2026-09-25';
  if (active === PIPELINE_RESTORED_20260928_FIXTURE_NAME) return '2026-09-28';
  return null;
}

export type BriefingVariant = 'morning' | 'eod';

export interface BriefingSummary {
  briefingDate: string;      // YYYY-MM-DD
  variant: BriefingVariant;
  headline: string;
  generatedAt: string;       // ISO-8601
  composerVersion?: string;
  videoS3Key?: string | null;
  videoStatus?: string | null;
}

export interface BriefingListResponse {
  variant: BriefingVariant;
  count: number;
  briefings: BriefingSummary[];
  isPremier: boolean;
  archivedCount?: number;    // rows hidden behind the free-window gate
}

export interface BriefingFull {
  briefingDate: string;
  variant: BriefingVariant;
  headline: string;
  generatedAt: string;
  composerVersion?: string;
  publicMarkdown: string;
  socialCaption?: string | null;   // ~60-90w for LinkedIn/X (always public)
  gatedMarkdown?: string;    // present only if isPremier
  videoS3Key?: string | null;
  videoStatus?: string | null;
  isPremier: boolean;
  gatedTeaser: boolean;      // true when anonymous or non-Premier — render upgrade CTA
  archiveLocked?: boolean;   // true when non-Premier requests a briefing older than the free window
}

const authHeaders = (token?: string) =>
  token ? { Authorization: `Bearer ${token}` } : {};

export const briefingsService = {
  async list(variant: BriefingVariant = 'eod', limit = 30, token?: string): Promise<BriefingListResponse> {
    const resp = await axios.get<BriefingListResponse>(
      `${API_BASE_URL}/briefings?variant=${variant}&limit=${limit}`,
      { headers: authHeaders(token) },
    );
    return resp.data;
  },

  async get(date: string, variant: BriefingVariant = 'eod', token?: string): Promise<BriefingFull> {
    const fx = fixtureLookup(date, variant);
    if (fx) return fx;
    const resp = await axios.get<BriefingFull>(
      `${API_BASE_URL}/briefings/${date}?variant=${variant}`,
      { headers: authHeaders(token) },
    );
    return resp.data;
  },

  async latest(variant: BriefingVariant = 'eod', token?: string): Promise<BriefingFull> {
    const latestDate = fixtureLatestDate();
    if (latestDate) {
      const fx = fixtureLookup(latestDate, variant);
      if (fx) return fx;
    }
    const resp = await axios.get<BriefingFull>(
      `${API_BASE_URL}/briefings/latest?variant=${variant}`,
      { headers: authHeaders(token) },
    );
    return resp.data;
  },
};
